import { FileText, Send, Eye, Check, X, Ban } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const eventAt = (acts, ev) => {
  const m = [...(acts || [])].reverse().find((a) => a.event === ev);
  return m?.at || null;
};

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

// Horizontal stage flow: Draft -> Sent -> Viewed -> R1..R5 -> Accepted/Rejected.
// Completed stages light up; unreached stages are muted; if signed early the
// remaining reminder nodes simply show as skipped.
export function ProposalStageFlow({ proposal }) {
  const acts = proposal.activity || [];
  const status = proposal.status;
  const stage = Number(proposal.reminder_stage || 0);
  const terminated = ["accepted", "rejected", "expired", "withdrawn", "superseded"].includes(status);

  const nodes = [];
  nodes.push({ key: "draft", label: "Draft", Icon: FileText, done: true, at: eventAt(acts, "created") || proposal.issue_date });
  const sentAt = eventAt(acts, "sent");
  const sentDone = !!sentAt || ["sent", "viewed", "acceptance_started", "accepted", "rejected", "expired", "withdrawn", "superseded"].includes(status);
  nodes.push({ key: "sent", label: "Sent", Icon: Send, done: sentDone, at: sentAt || proposal.sent_at });
  const viewedAt = eventAt(acts, "first_viewed");
  nodes.push({ key: "viewed", label: "Viewed", Icon: Eye, done: !!viewedAt, at: viewedAt });
  for (let i = 1; i <= 5; i++) {
    const at = eventAt(acts, `reminder_${i}`);
    nodes.push({ key: `r${i}`, label: `Reminder ${i}`, num: i, done: !!at || stage >= i, at });
  }
  if (status === "accepted") {
    nodes.push({ key: "accepted", label: "Accepted", Icon: Check, done: true, tone: "green", at: eventAt(acts, "accepted_signed") || eventAt(acts, "accepted_manual") || proposal.accepted_at });
  } else if (status === "rejected") {
    nodes.push({ key: "rejected", label: "Rejected", Icon: X, done: true, tone: "red", at: eventAt(acts, "rejected") });
  } else if (status === "expired" || status === "withdrawn" || status === "superseded") {
    nodes.push({ key: "term", label: status.charAt(0).toUpperCase() + status.slice(1), Icon: Ban, done: true, tone: "muted", at: eventAt(acts, status) });
  } else {
    nodes.push({ key: "decision", label: "Accepted / Rejected", Icon: Check, done: false, tone: "pending", at: null });
  }

  let lastDone = -1;
  nodes.forEach((n, i) => { if (n.done) lastDone = i; });

  const dotClass = (n, i) => {
    if (!n.done) return n.tone === "muted" ? "bg-muted text-cass-slate/40 border border-border" : "bg-white text-cass-slate/50 border border-dashed border-border";
    if (n.tone === "green") return "bg-emerald-600 text-white";
    if (n.tone === "red") return "bg-red-600 text-white";
    if (n.tone === "muted") return "bg-cass-slate/40 text-white";
    return `bg-cass-teal text-white${i === lastDone ? " ring-2 ring-cass-teal/30" : ""}`;
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex items-center overflow-x-auto no-scrollbar py-0.5" data-testid={`stage-flow-${proposal.id}`}>
        {nodes.map((n, i) => (
          <div key={n.key} className="flex items-center shrink-0">
            {i > 0 && <span className={`h-px w-2.5 shrink-0 ${nodes[i].done ? "bg-cass-teal" : "bg-border"}`} />}
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  data-testid={`stage-node-${proposal.id}-${n.key}${n.done ? "-done" : ""}`}
                  className={`grid place-items-center w-5 h-5 rounded-full text-[10px] font-semibold leading-none cursor-default ${dotClass(n, i)}`}
                >
                  {n.num != null ? n.num : n.Icon ? <n.Icon className="w-2.5 h-2.5" strokeWidth={2.5} /> : null}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" data-testid={`stage-tip-${proposal.id}-${n.key}`}>
                <div className="font-medium">{n.label}</div>
                <div className="text-[10px] opacity-80">{n.at ? fmt(n.at) : n.done ? "Completed" : "Pending"}</div>
              </TooltipContent>
            </Tooltip>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}

export default ProposalStageFlow;
